import React, { useState } from 'react';
import { previewUrl } from '../api/dicomApi';

const LAYOUTS = {
  '1x1': { cols: 1, rows: 1, label: '1×1' },
  '1x2': { cols: 2, rows: 1, label: '1×2' },
  '2x2': { cols: 2, rows: 2, label: '2×2' },
  '2x3': { cols: 3, rows: 2, label: '2×3' },
  '3x3': { cols: 3, rows: 3, label: '3×3' },
  '4x4': { cols: 4, rows: 4, label: '4×4' },
  '5x5': { cols: 5, rows: 5, label: '5×5' },
  '6x6': { cols: 6, rows: 6, label: '6×6' },
};

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export default function FilmPrintView({ files, onClose }) {
  const [layoutKey, setLayoutKey] = useState('2x2');
  const [showLabels, setShowLabels] = useState(true);
  const [orientation, setOrientation] = useState('landscape');

  if (!files || files.length === 0) return null;

  const layout = LAYOUTS[layoutKey];
  const perSheet = layout.cols * layout.rows;
  const sheets = chunk(files, perSheet);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 overflow-auto">
      <style>{`
        @media print {
          @page { size: A4 ${orientation}; margin: 10mm; }
          body * { visibility: hidden; }
          #film-print-area, #film-print-area * { visibility: visible; }
          #film-print-area { position: absolute; top: 0; left: 0; width: 100%; }
          .no-print { display: none !important; }
          .film-sheet { page-break-after: always; box-shadow: none !important; margin: 0 !important; }
          .film-sheet:last-child { page-break-after: auto; }
        }
      `}</style>

      <div className="no-print sticky top-0 bg-panel border-b border-line p-4 flex flex-wrap items-center gap-4 z-10">
        <h2 className="text-text font-medium mr-4 font-data text-sm">Film Print — {files.length} image{files.length !== 1 ? 's' : ''}</h2>

        <label className="flex items-center gap-2 text-sm text-textdim">
          Layout
          <select
            value={layoutKey}
            onChange={(e) => setLayoutKey(e.target.value)}
            className="bg-raised border border-line rounded-sm px-2 py-1 text-text focus:outline-none focus:border-phosphor"
          >
            {Object.entries(LAYOUTS).map(([key, v]) => (
              <option key={key} value={key}>{v.label} ({v.cols * v.rows}/sheet)</option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm text-textdim">
          Orientation
          <select
            value={orientation}
            onChange={(e) => setOrientation(e.target.value)}
            className="bg-raised border border-line rounded-sm px-2 py-1 text-text focus:outline-none focus:border-phosphor"
          >
            <option value="landscape">Landscape</option>
            <option value="portrait">Portrait</option>
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm text-textdim">
          <input type="checkbox" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} />
          Show patient labels
        </label>

        <div className="flex-1" />

        <span className="text-sm text-textdim font-data">{sheets.length} sheet{sheets.length !== 1 ? 's' : ''}</span>

        <button
          onClick={() => window.print()}
          className="px-4 py-2 rounded-sm bg-phosphor hover:bg-phosphordim text-ink text-sm font-semibold transition-colors"
        >
          Print / Save as PDF
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-sm bg-raised hover:bg-line text-textdim hover:text-text text-sm transition-colors"
        >
          Close
        </button>
      </div>

      <div id="film-print-area" className="p-6 flex flex-col items-center gap-8">
        {sheets.map((sheet, sheetIdx) => (
          <div
            key={sheetIdx}
            className="film-sheet bg-black shadow-xl p-4"
            style={{
              width: orientation === 'landscape' ? '277mm' : '190mm',
              minHeight: orientation === 'landscape' ? '190mm' : '277mm',
            }}
          >
            <div
              className="grid gap-2 h-full"
              style={{
                gridTemplateColumns: `repeat(${layout.cols}, 1fr)`,
                gridTemplateRows: `repeat(${layout.rows}, 1fr)`,
              }}
            >
              {sheet.map((file) => (
                <div key={file.id} className="flex flex-col items-center justify-center bg-black relative">
                  <img
                    src={previewUrl(file.id)}
                    alt={file.original_filename}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const el = e.target.nextElementSibling;
                      if (el) el.style.display = 'flex';
                    }}
                  />
                  <div
                    style={{ display: 'none' }}
                    className="w-full h-full min-h-[80px] items-center justify-center text-[9px] text-white/60 text-center px-2 border border-white/20"
                  >
                    No preview (compressed DICOM)
                  </div>
                  {showLabels && (
                    <div className="text-[9px] leading-tight text-white/90 mt-1 text-center w-full">
                      <div>{file.patient_name || 'Unknown'} · {file.patient_id || '—'}</div>
                      <div>{file.study_date || '—'} · {file.modality || ''} · {file.institution_name || ''}</div>
                    </div>
                  )}
                </div>
              ))}
              {/* Fill remaining empty cells so the grid stays consistent on the last sheet */}
              {Array.from({ length: perSheet - sheet.length }).map((_, i) => (
                <div key={`empty-${i}`} className="bg-black" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
